import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Spinner, ChartBar, Broadcast, Storefront, MapPin } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DataPagination } from './DataPagination';

const fmtAmt = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(String(s).slice(0, 10));
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};
const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export const WalletTxnSummaryTab = ({ mode }) => {
  const { isSuperAdmin } = useAuth();
  const { entities } = useTenant();

  const [entityId, setEntityId] = useState('');
  const [startDate, setStartDate] = useState(isoDaysAgo(6));
  const [endDate, setEndDate] = useState(isoDaysAgo(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  useEffect(() => {
    if (isSuperAdmin && !entityId && entities.length > 0) setEntityId(entities[0].id);
  }, [isSuperAdmin, entities, entityId]);

  const load = useCallback(async () => {
    if (isSuperAdmin && !entityId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (isSuperAdmin && entityId) params.set('entity_id', entityId);
      const { data: d } = await axios.get(`${API_URL}/api/wallet-transactions/summary?${params.toString()}`, {
        withCredentials: true, headers,
      });
      setData(d);
      setPage(1);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load summary'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, entityId, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const flatRows = useMemo(() => {
    const out = [];
    (data?.daily || []).forEach((day) => {
      const items = mode === 'recharge' ? (day.recharges || []) : mode === 'campus' ? (day.campuses || []) : (day.refills || []);
      items.forEach((it, i) => out.push({ ...it, date: day.date, _k: `${day.date}-${i}` }));
    });
    return out;
  }, [data, mode]);

  const totalPages = Math.max(1, Math.ceil(flatRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = flatRows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const labelKey = mode === 'recharge' ? 'channel' : mode === 'campus' ? 'campus_name' : 'store_name';
  const colLabel = mode === 'recharge' ? 'Channel' : mode === 'campus' ? 'Campus' : 'Store';
  const Icon = mode === 'recharge' ? Broadcast : mode === 'campus' ? MapPin : Storefront;
  const gridCols = mode === 'refill'
    ? 'grid-cols-[130px_1fr_110px_130px_150px]'
    : 'grid-cols-[130px_1fr_110px_150px]';
  const fmtVol = (v) => (v == null ? '—' : `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 })} L`);

  return (
    <div className="space-y-4" data-testid={`wt-summary-${mode}`}>
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3">
        {isSuperAdmin && (
          <div className="space-y-1">
            <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Entity</span>
            <Select value={entityId} onValueChange={setEntityId}>
              <SelectTrigger className="h-9 text-sm w-[220px] bg-white" data-testid={`wt-summary-entity-${mode}`}>
                <SelectValue placeholder="Select entity…" />
              </SelectTrigger>
              <SelectContent>
                {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1">
          <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">From</span>
          <Input type="date" value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} className="h-9 text-sm w-[150px] bg-white" data-testid={`wt-summary-from-${mode}`} />
        </div>
        <div className="space-y-1">
          <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">To</span>
          <Input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} className="h-9 text-sm w-[150px] bg-white" data-testid={`wt-summary-to-${mode}`} />
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center"><Spinner size={22} className="animate-spin text-[#0A2540] inline" /></div>
      ) : !data ? (
        <p className="text-sm text-[#78716C] py-10 text-center">No summary data.</p>
      ) : (
        <>
          {/* Daily breakdown */}
          <div className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden" data-testid={`wt-summary-table-${mode}`}>
            <div className="flex items-center gap-2.5 px-4 pt-4 pb-2">
              <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]">
                <Icon size={14} weight="fill" />
              </span>
              <p className="text-xs uppercase tracking-[0.18em] text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Daily Breakdown
              </p>
            </div>
            <div className={`grid ${gridCols} gap-2 items-center px-4 py-2.5 bg-[#FCFAF6] text-[10px] uppercase tracking-widest text-[#78716C] font-semibold`}>
              <span>Date</span>
              <span>{colLabel}</span>
              <span className="text-right">Count</span>
              {mode === 'refill' && <span className="text-right">Volume</span>}
              <span className="text-right">Amount</span>
            </div>
            {flatRows.length === 0 ? (
              <div className="py-12 text-center">
                <ChartBar size={20} className="text-slate-400 inline mb-2" />
                <p className="text-xs text-[#78716C]">No summary rows for this period. The summary job runs daily — try widening the date range.</p>
              </div>
            ) : (
              <div className="divide-y divide-[#EDE6D8]">
                {paged.map((r) => (
                  <div key={r._k} className={`grid ${gridCols} gap-2 items-center px-4 py-3`} data-testid={`wt-summary-row-${r._k}`}>
                    <span className="text-xs font-mono text-[#78716C]">{fmtDate(r.date)}</span>
                    <span className="text-sm font-medium text-[#1C1917] truncate">{r[labelKey]}</span>
                    <span className="text-sm font-mono text-[#78716C] text-right">{Number(r.count).toLocaleString()}</span>
                    {mode === 'refill' && (
                      <span className="text-sm font-mono text-[#0E7490] text-right">{fmtVol(r.volume)}</span>
                    )}
                    <span className="text-sm font-mono font-semibold text-[#0A2540] text-right">{fmtAmt(r.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DataPagination
            page={safePage}
            pageSize={pageSize}
            total={flatRows.length}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
            testId={`wt-summary-pagination-${mode}`}
          />
        </>
      )}
    </div>
  );
};
